<template>
  <div class="px-4 sm:px-6 lg:px-8">
    <NotificationPopup ref="notification" :type="notificationType" :title="notificationTitle" :text="notificationText" />
    <InvestorVerificationSlideover
      v-if="showDocumentsSlideover"
      :show="showDocumentsSlideover"
      :account="selected_investor_account"
      :user_id="userStore.user.id"
      @closed="showDocumentsSlideover = false"
      @silent_success="getInvestorAccounts()"
      @success="
        notify('success', 'Updated Successfully', 'The investors kyc and aml statuses have been updated successfully');
        getInvestorAccounts();
      "
      @aisuccess="
        notify('success', 'Updated Successfully', 'The investors accreditation status has been updated successfully');
        getInvestorAccounts();
      "
      @requested="notify('success', 'Manual Review Requested', 'Your request has been sent, the investor will be reviewed shortly by our team.')"
      @error="notify('failure', 'Sorry', 'Something went wrong and we were unable to update the investors kyc and aml status')"
      @aierror="notify('failure', 'Full Name Required', 'In order to update accreditation status, You must first complete your profile in the Settings tab')"
    />

    <!-- Header -->
    <div>
      <div class="border-b border-gray-200 pb-5 mb-5 flex items-center justify-between flex-wrap gap-5 dark:border-neutral-700">
        <!-- Tab Header -->
        <h3 class="primary_header">Investors</h3>

        <div class="flex items-center flex-wrap gap-4">
          <!-- Search -->
          <SearchInput v-model="query" />
          <!-- Filter -->
          <AppDropdown
            title="Quick Filter"
            :items="[
              'Today',
              'This Week',
              'Accreditation - Accredited',
              'Accreditation - Under Review',
              'Accreditation - Action Required',
              'Accreditation - Not Accredited',
              'KYC & AML - Approved',
              'KYC & AML - Pending',
              'KYC & AML - Disapproved',
              'Approved & Accredited',
            ]"
            :icon="AdjustmentsHorizontalIcon"
            @selected="handleFilter"
          />
          <AppButtonPrimary v-if="usingFilter" text="Clear Filters" size="lg" :icon="XMarkIcon" @click="handleFilter('Clear Filters')" />
        </div>
      </div>
      <div class="flex">
        <TableOrCard v-model="isTable" />
      </div>
    </div>

    <!-- Investor Verification Table -->
    <AppExportableTable
      v-if="isTable"
      v-model="columns"
      :loading="loading_first_investors"
      :loading_all="loading_all_investors"
      :rows="filteredInvestors"
      :is-clickable="true"
      class="-mt-8"
      export_title="needs_verification"
      @click-primary="handleInvestorClick"
    />

    <!-- Investor Verification Cards -->
    <div v-else role="list" class="flex gap-6 flex-wrap mt-4">
      <div
        v-for="i in filteredInvestors"
        :key="i.id"
        class="divide-y divide-gray-200 rounded-lg bg-white shadow-opera dark:shadow-none cursor-pointer border w-96 border-white hover:border-gray-300 hover:border dark:bg-neutral-800 dark:border-neutral-800 dark:hover:border-neutral-600"
        @click="handleInvestorClick(i)"
      >
        <VerificationCard :investor="i" :role="userStore.user.profiles_protected.role" />
      </div>
    </div>
  </div>
</template>

<script setup>
// Essentials
import { onMounted, computed, ref } from "vue";

// Components
import InvestorVerificationSlideover from "@/components/investors/InvestorVerificationSlideover.vue";
import NotificationPopup from "@/components/applicationui/AppNotification.vue";
import VerificationCard from "@/components/investors/VerificationCard.vue";
import AppExportableTable from "@/components/applicationui/tables/AppExportableTable.vue";
import SearchInput from "@/components/applicationinputs/SearchInput.vue";
import TableOrCard from "@/components/applicationui/tables/TableOrCard.vue";
import AppDropdown from "@/components/applicationinputs/AppDropdown.vue";
import AppButtonPrimary from "@/components/applicationui/AppButtonPrimary.vue";

// Icons
import { AdjustmentsHorizontalIcon, XMarkIcon } from "@heroicons/vue/24/outline";

// Stores
import { useUserStore } from "@/stores/user";
const userStore = useUserStore();
import { useAccountsStore } from "@/stores/accounts";
const accountsStore = useAccountsStore();
import { usePartnerStore } from "@/stores/partner";
const partnerStore = usePartnerStore();

// Table builder START
const isTable = ref(true);
if (window.innerWidth < 1024) isTable.value = false;
const columns = ref([
  { key: "parties.first_name", key2: "parties.last_name", label: "Name", type: "two-strings", visible: false },
  { key: "parties.contact_email", label: "Email", type: "bold", visible: true },
  { key: "all_parties_kyc_status", label: "KYC Status", type: "badge", visible: true },
  { key: "all_parties_aml_status", label: "AML Status", type: "badge", visible: true },
  {
    key: "parties.parties_protected.aml_updated_at",
    label: "AML Updated",
    type: "date",
    visible: true,
  },
  {
    key: "accounts_protected.accredited_status",
    label: "Accredited Status",
    type: "badge",
    visible: true,
  },
  {
    key: "tapi_account_id",
    label: "Account ID",
    type: "copy",
    visible: true,
  },
  {
    key: "parties.tapi_party_id",
    label: "Primary Party ID",
    type: "copy",
    visible: false,
  },
  {
    key: "secondary_party.tapi_party_id",
    label: "Secondary Party ID",
    type: "copy",
    visible: false,
  },
  {
    key: "partners",
    label: "Partner",
    type: "partner-logo",
    visible: false,
  },
  { key: "created_at", label: "Created", type: "date", visible: true },
]);
// Table builder END

// Filter START
const usingFilter = ref(false);
const stored_investors = ref([]);
const handleFilter = (filter) => {
  if (!usingFilter.value) {
    stored_investors.value = investor_accounts.value;
  }
  if (filter === "Clear Filters") {
    investor_accounts.value = stored_investors.value;
    stored_investors.value = [];
    usingFilter.value = false;
    return;
  }

  let temp_investors = investor_accounts.value;
  if (usingFilter.value) {
    temp_investors = stored_investors.value;
  }
  usingFilter.value = true;

  if (filter === "Today") {
    investor_accounts.value = temp_investors.filter((i) => {
      const createdAtDate = new Date(i.created_at);
      const now = new Date();
      const twentyFourHoursAgo = new Date(now.getTime() - 24 * 60 * 60 * 1000);
      return createdAtDate >= twentyFourHoursAgo && createdAtDate <= now;
    });
  }
  if (filter === "This Week") {
    investor_accounts.value = temp_investors.filter((i) => {
      const createdAtDate = new Date(i.created_at);
      const today = new Date();
      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(today.getDate() - 7);
      return createdAtDate >= sevenDaysAgo && createdAtDate <= today;
    });
  }
  if (filter === "Accreditation - Accredited") {
    investor_accounts.value = temp_investors.filter((i) => {
      return i.accounts_protected.accredited_status === "Accredited";
    });
  }
  if (filter === "Accreditation - Under Review") {
    investor_accounts.value = temp_investors.filter((i) => {
      return i.accounts_protected.accredited_status === "Under Review";
    });
  }
  if (filter === "Accreditation - Action Required") {
    investor_accounts.value = temp_investors.filter((i) => {
      return i.accounts_protected.accredited_status === "Action Required";
    });
  }
  if (filter === "Accreditation - Not Accredited") {
    investor_accounts.value = temp_investors.filter((i) => {
      return i.accounts_protected.accredited_status === "Not Accredited";
    });
  }
  if (filter === "KYC & AML - Approved") {
    investor_accounts.value = temp_investors.filter((i) => {
      return i.all_parties_kyc_status === "approved" && i.all_parties_aml_status === "approved";
    });
  }
  if (filter === "KYC & AML - Pending") {
    investor_accounts.value = temp_investors.filter((i) => {
      return i.all_parties_kyc_status.includes("pending") || i.all_parties_aml_status.includes("pending");
    });
  }
  if (filter === "KYC & AML - Disapproved") {
    investor_accounts.value = temp_investors.filter((i) => {
      return i.all_parties_kyc_status.includes("disapproved") || i.all_parties_aml_status.includes("disapproved");
    });
  }
  if (filter === "Approved & Accredited") {
    investor_accounts.value = temp_investors.filter((i) => {
      return (
        i.all_parties_kyc_status === "approved" && i.all_parties_aml_status === "approved" && i.accounts_protected.accredited_status === "Accredited"
      );
    });
  }
};
// Filter END

// Search START
const query = ref("");
const filteredInvestors = computed(() => {
  return query.value === ""
    ? investor_accounts.value
    : investor_accounts.value.filter((investor) => {
        // Party
        if (investor.parties?.contact_email?.toLowerCase().includes(query.value.toLowerCase())) return true;
        if (investor.parties?.first_name?.toLowerCase().includes(query.value.toLowerCase())) return true;
        if (investor.parties?.last_name?.toLowerCase().includes(query.value.toLowerCase())) return true;
        if (investor.parties?.phone?.toLowerCase().includes(query.value.toLowerCase())) return true;
        if (investor.parties?.zip?.toLowerCase().includes(query.value.toLowerCase())) return true;
        if (investor.parties?.id?.toLowerCase().includes(query.value.toLowerCase())) return true;
        if (investor.parties?.tapi_party_id?.toLowerCase().includes(query.value.toLowerCase())) return true;
        if (investor.secondary_party?.id?.toLowerCase().includes(query.value.toLowerCase())) return true;
        if (investor.secondary_party?.tapi_party_id?.toLowerCase().includes(query.value.toLowerCase())) return true;
        // Account
        if (investor.name?.toLowerCase().includes(query.value.toLowerCase())) return true;
        if (investor.id?.toLowerCase().includes(query.value.toLowerCase())) return true;
        if (investor.user_id?.toLowerCase().includes(query.value.toLowerCase())) return true;
        if (investor.tapi_account_id?.toLowerCase().includes(query.value.toLowerCase())) return true;
      });
});
// Search END

// Click on investor START
const showDocumentsSlideover = ref(false);
const selected_investor_account = ref(null);
const handleInvestorClick = (investor_account) => {
  selected_investor_account.value = investor_account;
  showDocumentsSlideover.value = true;
};
// Click on investor END

// Get investor accounts START
const investor_accounts = ref([]);
const loading_first_investors = ref(true);
const loading_all_investors = ref(true);
async function getInvestorAccounts() {
  loading_first_investors.value = true;
  loading_all_investors.value = true;

  let partner_id = null;
  if (userStore.user.profiles_protected.role != "super_admin") {
    partner_id = partnerStore.partner.id;
  }

  const first_investors = await accountsStore.fetchAccountsForInvestorVerificationByPartnerPaginated(0, 49, partner_id);

  if (!first_investors) {
    loading_first_investors.value = false;
    loading_all_investors.value = false;
    return;
  }

  first_investors.forEach((account) => {
    if (account.secondary_party_id) {
      setMixedAccountKycStatuses(account);
      setMixedAccountAmlStatuses(account);
    } else {
      account.all_parties_kyc_status = account.parties.parties_protected.kyc;
      account.all_parties_aml_status = account.parties.parties_protected.aml;
    }
  });

  investor_accounts.value = first_investors;
  loading_first_investors.value = false;

  getRestOfInvestorAccounts(partner_id);
}

async function getRestOfInvestorAccounts(partner_id) {
  if (investor_accounts.value.length < 49) {
    loading_all_investors.value = false;
    return;
  }
  const rest_of_investor_accounts = await accountsStore.fetchAccountsForInvestorVerificationByPartnerPaginated(50, 10000000, partner_id);

  if (!rest_of_investor_accounts) {
    loading_all_investors.value = false;
    return;
  }

  rest_of_investor_accounts.forEach((account) => {
    if (account.secondary_party_id) {
      setMixedAccountKycStatuses(account);
      setMixedAccountAmlStatuses(account);
    } else {
      account.all_parties_kyc_status = account.parties.parties_protected.kyc;
      account.all_parties_aml_status = account.parties.parties_protected.aml;
    }
  });

  investor_accounts.value = investor_accounts.value.concat(rest_of_investor_accounts);
  loading_all_investors.value = false;
  usingFilter.value = false;
}
const setMixedAccountKycStatuses = (account) => {
  if (account.parties.parties_protected.kyc == "approved") {
    if (account.secondary_party.parties_protected.kyc === "pending") {
      account.all_parties_kyc_status = "pending (secondary)";
    } else if (account.secondary_party.parties_protected.kyc === "disapproved") {
      account.all_parties_kyc_status = "disapproved (secondary)";
    } else {
      account.all_parties_kyc_status = "approved";
    }
  }
  if (account.parties.parties_protected.kyc == "pending") {
    if (account.secondary_party.parties_protected.kyc === "pending") {
      account.all_parties_kyc_status = "pending (all)";
    } else if (account.secondary_party.parties_protected.kyc === "disapproved") {
      account.all_parties_kyc_status = "disapproved (secondary)";
    } else {
      account.all_parties_kyc_status = "pending (primary)";
    }
  }
  if (account.parties.parties_protected.kyc == "disapproved") {
    if (account.secondary_party.parties_protected.kyc === "pending") {
      account.all_parties_kyc_status = "disapproved (primary)";
    } else if (account.secondary_party.parties_protected.kyc === "disapproved") {
      account.all_parties_kyc_status = "disapproved (all)";
    } else {
      account.all_parties_kyc_status = "disapproved (primary)";
    }
  }
};
const setMixedAccountAmlStatuses = (account) => {
  if (account.parties.parties_protected.aml == "approved") {
    if (account.secondary_party.parties_protected.aml === "pending") {
      account.all_parties_aml_status = "pending (secondary)";
    } else if (account.secondary_party.parties_protected.aml === "disapproved") {
      account.all_parties_aml_status = "disapproved (secondary)";
    } else {
      account.all_parties_aml_status = "approved";
    }
  }
  if (account.parties.parties_protected.aml == "pending") {
    if (account.secondary_party.parties_protected.aml === "pending") {
      account.all_parties_aml_status = "pending (all)";
    } else if (account.secondary_party.parties_protected.aml === "disapproved") {
      account.all_parties_aml_status = "disapproved (secondary)";
    } else {
      account.all_parties_aml_status = "pending (primary)";
    }
  }
  if (account.parties.parties_protected.aml == "disapproved") {
    if (account.secondary_party.parties_protected.aml === "pending") {
      account.all_parties_aml_status = "disapproved (primary)";
    } else if (account.secondary_party.parties_protected.aml === "disapproved") {
      account.all_parties_aml_status = "disapproved (all)";
    } else {
      account.all_parties_aml_status = "disapproved (primary)";
    }
  }
};
// Get investor accounts END

// Notify START
const notification = ref(null);
const notificationType = ref("succes");
const notificationTitle = ref("Success");
const notificationText = ref("");
const notify = (nType, nTitle, nText) => {
  notificationType.value = nType;
  notificationTitle.value = nTitle;
  notificationText.value = nText;
  notification.value.show();
};
// Notify END

// Mounted START
const loading = ref(true);
onMounted(async () => {
  // Get all investors accounts
  await getInvestorAccounts();

  loading.value = false;
});
// Mounted END
</script>
